import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const GeometryBuilder = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState('');
  const [shapesBuilt, setShapesBuilt] = useState(0);

  const generateQuestion = () => {
    const types = [
      { type: 'rectangle', length: 8, width: 5, question: 'perimeter', answer: 26, unit: 'cm' },
      { type: 'rectangle', length: 6, width: 4, question: 'area', answer: 24, unit: 'sq cm' },
      { type: 'square', side: 7, question: 'perimeter', answer: 28, unit: 'cm' },
      { type: 'square', side: 5, question: 'area', answer: 25, unit: 'sq cm' },
      { type: 'rectangle', length: 10, width: 3, question: 'perimeter', answer: 26, unit: 'cm' },
      { type: 'rectangle', length: 9, width: 4, question: 'area', answer: 36, unit: 'sq cm' },
    ];
    const q = types[Math.floor(Math.random() * types.length)];
    setQuestion(q);
    setUserAnswer('');
    setFeedback('');
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question) return;

    const answer = parseInt(userAnswer);
    if (answer === question.answer) {
      setScore(score + 15);
      setShapesBuilt(shapesBuilt + 1);
      setFeedback('✅ Perfect!');
      setTimeout(generateQuestion, 1500);
    } else {
      setFeedback(`❌ Wrong! Answer: ${question.answer} ${question.unit}`);
      setTimeout(generateQuestion, 2500);
    }
  };

  const getQuestionText = () => {
    if (!question) return '';
    if (question.type === 'rectangle') {
      return `Rectangle: Length = ${question.length}cm, Width = ${question.width}cm. Find ${question.question}:`;
    } else {
      return `Square: Side = ${question.side}cm. Find ${question.question}:`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-teal-400 to-blue-500 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 mb-6 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="hover:text-yellow-300">← Back</button>
            <h1 className="text-3xl font-bold">📏 Geometry Builder</h1>
            <div className="text-2xl font-bold">{score} pts</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">📐</div>
            <h2 className="text-2xl font-bold text-gray-800">Calculate Perimeter & Area!</h2>
            <p className="text-gray-600">Shapes Built: {shapesBuilt}</p>
          </div>

          {question && (
            <div className="text-center">
              {/* Visual Shape */}
              <div className="mb-6 flex justify-center">
                {question.type === 'rectangle' ? (
                  <div className="relative">
                    <div 
                      className="border-4 border-blue-600 bg-blue-100"
                      style={{ 
                        width: `${question.length * 20}px`, 
                        height: `${question.width * 20}px` 
                      }}
                    >
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-sm font-bold">
                        {question.length}cm
                      </div>
                      <div className="absolute top-1/2 -left-8 transform -translate-y-1/2 text-sm font-bold">
                        {question.width}cm
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div 
                      className="border-4 border-green-600 bg-green-100"
                      style={{ 
                        width: `${question.side * 20}px`, 
                        height: `${question.side * 20}px` 
                      }}
                    >
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-sm font-bold">
                        {question.side}cm
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-xl font-bold text-gray-700 mb-6">{getQuestionText()}</div>
              
              <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                <input
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  className="w-full text-3xl text-center border-4 border-green-300 rounded-lg p-4 mb-4 focus:border-green-500 focus:outline-none"
                  placeholder="Answer"
                  autoFocus
                />
                <button type="submit" className="w-full bg-green-600 text-white text-xl font-bold py-4 rounded-lg hover:bg-green-700">
                  Build Shape!
                </button>
              </form>
              
              {feedback && (
                <div className={`mt-4 text-xl font-bold ${feedback.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                  {feedback}
                </div>
              )}
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-gray-700">
            <p><strong>💡 Formulas:</strong></p>
            <p>Rectangle Perimeter = 2 × (length + width)</p>
            <p>Rectangle Area = length × width</p>
            <p>Square Perimeter = 4 × side</p>
            <p>Square Area = side × side</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeometryBuilder;